export type SongLibraryGuideStepId =
    | "open_song"
    | "switch_stem"
    | "open_multitrack"
    | "close_multitrack"
    | "add_to_rehearsal"
    | "view_in_rehearsal";

export interface SongLibraryGuideState {
    completed: SongLibraryGuideStepId[];
    dismissed_at: string | null;
    completed_at: string | null;
}

export interface SongLibraryGuideRuntimeState {
    isAudioPlayerOpen: boolean;
    isMultitrackOpen: boolean;
}

export const SONG_LIBRARY_GUIDE_STEPS: SongLibraryGuideStepId[] = [
    "open_song",
    "switch_stem",
    "open_multitrack",
    "close_multitrack",
    "add_to_rehearsal",
    "view_in_rehearsal",
];

export function createDefaultSongLibraryGuideState(
    seed?: Partial<SongLibraryGuideState> | null
): SongLibraryGuideState {
    const completed = Array.isArray(seed?.completed)
        ? seed!.completed.filter((step): step is SongLibraryGuideStepId => SONG_LIBRARY_GUIDE_STEPS.includes(step as SongLibraryGuideStepId))
        : [];

    // Backward compatibility: older guide versions did not include close_multitrack.
    if (
        !completed.includes("close_multitrack")
        && (completed.includes("add_to_rehearsal") || completed.includes("view_in_rehearsal"))
    ) {
        completed.push("close_multitrack");
    }

    return {
        completed,
        dismissed_at: seed?.dismissed_at ?? null,
        completed_at: seed?.completed_at ?? null,
    };
}

export function getSongLibraryGuideNextStep(state: SongLibraryGuideState): SongLibraryGuideStepId | null {
    const completed = new Set(state.completed || []);
    return SONG_LIBRARY_GUIDE_STEPS.find((step) => !completed.has(step)) || null;
}

export function resolveActionableSongLibraryGuideStep(
    nextStep: SongLibraryGuideStepId | null,
    runtime: SongLibraryGuideRuntimeState
): SongLibraryGuideStepId | null {
    if (!nextStep) return null;

    switch (nextStep) {
        case "open_song":
            return "open_song";
        case "switch_stem":
            return runtime.isAudioPlayerOpen ? "switch_stem" : "open_song";
        case "open_multitrack":
            return runtime.isAudioPlayerOpen ? "open_multitrack" : "open_song";
        case "close_multitrack":
            if (!runtime.isAudioPlayerOpen) return "open_song";
            return runtime.isMultitrackOpen ? "close_multitrack" : "open_multitrack";
        case "add_to_rehearsal":
            return runtime.isMultitrackOpen ? "close_multitrack" : "add_to_rehearsal";
        case "view_in_rehearsal":
            return runtime.isMultitrackOpen ? "close_multitrack" : "view_in_rehearsal";
        default:
            return nextStep;
    }
}

export function isSongLibraryGuideCompleted(state: SongLibraryGuideState): boolean {
    return getSongLibraryGuideNextStep(state) === null;
}

export function getSongLibraryGuideStepNumber(step: SongLibraryGuideStepId | null): number {
    if (!step) return 0;
    const index = SONG_LIBRARY_GUIDE_STEPS.indexOf(step);
    return index >= 0 ? index + 1 : 0;
}

export function getSongLibraryGuideProgress(state: SongLibraryGuideState): {completed: number; total: number} {
    return {
        completed: (state.completed || []).length,
        total: SONG_LIBRARY_GUIDE_STEPS.length,
    };
}

export function markSongLibraryGuideStep(
    state: SongLibraryGuideState,
    step: SongLibraryGuideStepId
): SongLibraryGuideState {
    if (state.completed.includes(step)) {
        return state;
    }
    const nextCompleted = [...state.completed, step];
    const done = nextCompleted.length >= SONG_LIBRARY_GUIDE_STEPS.length;
    return {
        ...state,
        completed: nextCompleted,
        completed_at: done ? state.completed_at || new Date().toISOString() : state.completed_at,
    };
}
